import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { mockFeaturedReviews } from "../../../mocks/reviews";
import { mockNews } from "../../../mocks/news";
import { useCategories } from "../../../hooks/useCategories";
import { useApp } from "../../../contexts/AppContext";
import { supabase } from "../../../lib/supabase";

const PROMO_FORM_URL = "https://readdy.ai/api/form/d73or1ecsbv12mkucq30";

function PromoRequestModal({ onClose }: { onClose: () => void }) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const form = e.currentTarget;
      const data = new FormData(form);
      const body = new URLSearchParams();
      data.forEach((v, k) => body.append(k, String(v)));
      await fetch(PROMO_FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl p-8 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        {submitted ? (
          <div className="text-center py-10">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-teal/10 mx-auto mb-5">
              <i className="ri-checkbox-circle-fill text-4xl text-teal" />
            </div>
            <h3 className="font-serif text-deep-green text-xl mb-3">申請を受け付けました</h3>
            <p className="text-sm text-charcoal/60 font-sans leading-relaxed mb-6">
              管理者が内容を確認後、お知らせ欄に掲載いたします。<br />掲載まで通常2〜5営業日いただきます。
            </p>
            <button onClick={onClose} className="px-8 py-3 bg-deep-green text-white rounded-full text-sm font-sans cursor-pointer hover:bg-teal transition-colors whitespace-nowrap">
              閉じる
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-gold text-xs font-display italic tracking-widest mb-1">Notice Application</p>
                <h3 className="font-serif text-deep-green text-xl">お知らせ掲載の申請</h3>
              </div>
              <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-full bg-stone-100 text-charcoal/40 cursor-pointer hover:text-charcoal transition-colors">
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <p className="text-xs text-charcoal/50 font-sans mb-6 leading-relaxed">
              イベント・特集・キャンペーン情報をサイトのお知らせ欄に掲載できます。<br />
              内容を送信すると、管理者が確認の上、掲載します。
            </p>
            <form data-readdy-form id="home-promo-request-form" onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-sans text-charcoal/60 mb-1.5">事業者名・サービス名 <span className="text-red-400">*</span></label>
                <input name="businessName" required className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm font-sans outline-none focus:border-teal transition-colors" placeholder="例：しみ抜き専門店 染田" />
              </div>
              <div>
                <label className="block text-xs font-sans text-charcoal/60 mb-1.5">掲載ジャンル <span className="text-red-400">*</span></label>
                <select name="category" required className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm font-sans outline-none focus:border-teal transition-colors bg-white cursor-pointer">
                  <option value="">選択してください</option>
                  <option value="イベント">イベント</option>
                  <option value="特集">特集</option>
                  <option value="キャンペーン">キャンペーン</option>
                  <option value="お知らせ">お知らせ</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-sans text-charcoal/60 mb-1.5">掲載タイトル <span className="text-red-400">*</span></label>
                <input name="title" required maxLength={80} className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm font-sans outline-none focus:border-teal transition-colors" placeholder="例：夏の和服ケア特集 開催決定" />
              </div>
              <div>
                <label className="block text-xs font-sans text-charcoal/60 mb-1.5">掲載内容 <span className="text-red-400">*</span></label>
                <textarea name="detail" required maxLength={500} rows={4} className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm font-sans outline-none focus:border-teal transition-colors resize-none" placeholder="掲載したい内容の詳細（500文字以内）" />
              </div>
              <div>
                <label className="block text-xs font-sans text-charcoal/60 mb-1.5">連絡先メールアドレス <span className="text-red-400">*</span></label>
                <input name="email" type="email" required className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm font-sans outline-none focus:border-teal transition-colors" placeholder="your@email.com" />
              </div>
              <button type="submit" disabled={submitting} className="w-full bg-deep-green text-white py-3 rounded-full text-sm font-sans cursor-pointer hover:bg-teal transition-colors whitespace-nowrap disabled:opacity-60">
                {submitting ? "送信中..." : "申請を送信する →"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export function AnnouncementBanner() {
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const d = localStorage.getItem("tsugite_announcement_v1");
    if (d) setDismissed(true);
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem("tsugite_announcement_v1", "1");
  };

  if (dismissed) return null;

  return (
    <div className="bg-deep-green text-white px-4 py-3 relative z-50">
      <div className="max-w-screen-xl mx-auto flex items-center justify-center gap-4">
        <div className="flex items-center gap-3 flex-wrap justify-center">
          <span className="text-gold text-xs font-display italic tracking-widest">NEW</span>
          <p className="text-sm font-sans font-medium text-white">
            🎉 <strong>2025年4月1日よりTSUGITEが正式サービス開始！</strong> 今なら事業者登録が無料で、手数料は売れてから。
          </p>
          <Link
            to="/register"
            className="text-xs bg-gold text-white px-4 py-1.5 rounded-full font-sans hover:bg-amber-400 transition-colors whitespace-nowrap cursor-pointer"
          >
            無料で始める →
          </Link>
        </div>
        <button
          onClick={handleDismiss}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-full hover:bg-white/10 cursor-pointer transition-colors flex-shrink-0"
        >
          <i className="ri-close-line text-white/60 text-base" />
        </button>
      </div>
    </div>
  );
}

export function CategoryNavSection() {
  const { categories, loading } = useCategories();
  const [active, setActive] = useState("すべて");
  const navigate = useNavigate();

  const handleCategoryClick = (catName: string, catId?: string) => {
    setActive(catName);
    if (catName === "すべて") {
      navigate("/categories");
    } else if (catId) {
      navigate(`/categories/${catId}`);
    }
  };

  return (
    <section className="bg-cream py-10 px-4">
      <div className="max-w-screen-xl mx-auto">
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          <button
            onClick={() => handleCategoryClick("すべて")}
            className={`px-4 py-2 rounded-full text-sm font-sans whitespace-nowrap cursor-pointer transition-all duration-300 border ${active === "すべて" ? "bg-deep-green text-white border-deep-green" : "bg-transparent text-deep-green border-deep-green/30 hover:border-deep-green"}`}
          >
            すべて
          </button>
          {!loading && categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => handleCategoryClick(cat.name, cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-sans whitespace-nowrap cursor-pointer transition-all duration-300 border ${active === cat.name ? "bg-deep-green text-white border-deep-green" : "bg-transparent text-deep-green border-deep-green/30 hover:border-deep-green"}`}
            >
              {cat.name}
            </button>
          ))}
          <Link to="/categories" className="px-4 py-2 rounded-full text-sm font-sans whitespace-nowrap cursor-pointer border border-gold text-gold hover:bg-gold hover:text-white transition-all duration-300">+ もっと見る</Link>
        </div>
      </div>
    </section>
  );
}

export function HowItWorksSection() {
  const steps = [
    { num: "01", title: "事業者を探す", desc: "気になる職人・専門家を見つけ、無料サンプルで技術の深さを確認しましょう。", icon: "ri-search-eye-line", href: "/providers", dark: true },
    { num: "02", title: "購入して学ぶ", desc: "動画講座・教材キットで自分のペースで実践的に学べます。何度でも見返せます。", icon: "ri-play-circle-line", href: "/categories", dark: false },
    { num: "03", title: "職人とつながる", desc: "購入後はメッセージで直接質問。フォローで最新講座・更新情報を受け取る。", icon: "ri-message-3-line", href: "/mypage", dark: false },
  ];
  return (
    <section className="bg-white py-20 px-4">
      <div className="max-w-screen-xl mx-auto">
        <div className="mb-12">
          <p className="text-gold text-xs font-display italic tracking-widest mb-2">How It Works</p>
          <h2 className="font-sans text-deep-green text-4xl md:text-5xl font-light">学びの流れ</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {steps.map((s) => (
            <div key={s.num} className={`rounded-2xl p-8 border ${s.dark ? "bg-deep-green border-deep-green text-white" : "bg-ivory border-deep-green/20 text-deep-green"} transition-all duration-300 hover:-translate-y-1`}>
              <div className={`w-12 h-12 flex items-center justify-center rounded-full mb-6 ${s.dark ? "bg-white/10" : "bg-deep-green/8"}`}>
                <i className={`${s.icon} text-2xl ${s.dark ? "text-gold" : "text-teal"}`} />
              </div>
              <p className={`font-display text-4xl text-gold/25 leading-none mb-2 font-light`}>{s.num}</p>
              <h3 className={`font-serif text-xl mb-3 ${s.dark ? "text-white" : "text-deep-green"}`}>{s.title}</h3>
              <p className={`text-sm font-sans leading-relaxed mb-6 ${s.dark ? "text-white/70" : "text-charcoal/60"}`}>{s.desc}</p>
              <Link to={s.href} className={`text-sm font-sans cursor-pointer transition-colors ${s.dark ? "text-gold hover:text-light-gold" : "text-teal hover:text-deep-green"}`}>{s.title}へ →</Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ReviewSection() {
  return (
    <section className="bg-ivory py-20 px-4">
      <div className="max-w-screen-xl mx-auto">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="text-gold text-xs font-display italic tracking-widest mb-2">Reviews</p>
            <h2 className="font-sans text-deep-green text-3xl md:text-4xl font-light">受講者の声</h2>
          </div>
          <Link to="/reviews" className="text-charcoal/50 text-xs font-sans hidden sm:block hover:text-teal transition-colors cursor-pointer">全てのレビューを見る →</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {mockFeaturedReviews.map((review) => (
            <div key={review.id} className="bg-white rounded-2xl p-7 flex flex-col border border-stone-100 hover:-translate-y-1 transition-transform duration-300">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(review.rating)].map((_, i) => <span key={i} className="star-gold text-lg">★</span>)}
              </div>
              <p className="font-display text-5xl text-gold/25 leading-none mb-2 font-light">"</p>
              <p className="text-charcoal/80 font-sans text-sm leading-relaxed flex-1 mb-6">{review.content}</p>
              <div className="border-t border-stone-100 pt-4">
                <p className="font-sans text-charcoal text-sm font-medium">{review.authorName}</p>
                <p className="text-charcoal/50 text-xs font-sans mt-0.5">{review.authorRole}</p>
                <div className="flex items-center gap-2 mt-2">
                  <i className="ri-play-circle-line text-teal text-xs" />
                  <p className="text-teal text-xs font-sans">{review.courseName}</p>
                </div>
                <p className="text-charcoal/30 text-xs font-sans mt-1">{review.date}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CtaSection() {
  const { isLoggedIn, setMode } = useApp();
  const navigate = useNavigate();

  const handleSell = () => {
    if (isLoggedIn) {
      setMode("provider");
      navigate("/mypage/provider");
    } else {
      navigate("/register");
    }
  };

  return (
    <section className="bg-cream py-20 px-4 text-center">
      <div className="max-w-2xl mx-auto">
        <p className="text-gold text-xs font-display italic tracking-widest mb-4">For Artisans &amp; Professionals</p>
        <h2 className="font-serif text-deep-green text-3xl md:text-4xl font-light leading-relaxed mb-4">あなたの技術を、<br />次の世代へ。</h2>
        <p className="text-charcoal/60 text-sm font-sans leading-relaxed mb-8">
          業種を問わず、あなたの専門技術を必要としている人がいます。<br className="hidden md:block" />
          無料で出店でき、手数料は売れてから。まず、始めてみてください。
        </p>
        <button onClick={handleSell} className="btn-primary inline-block text-sm px-10 py-4 cursor-pointer whitespace-nowrap">無料で出店する →</button>
        <div className="mt-10 grid grid-cols-3 gap-4 max-w-md mx-auto">
          {[{ v: "0円", l: "初期費用" }, { v: "20%〜", l: "販売手数料" }, { v: "全業種", l: "対応ジャンル" }].map((s) => (
            <div key={s.l} className="bg-white rounded-xl p-4">
              <p className="font-serif text-deep-green text-xl">{s.v}</p>
              <p className="text-xs text-charcoal/50 font-sans mt-1">{s.l}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function NewsSection() {
  const [activeNewsTab, setActiveNewsTab] = useState<"all" | "event" | "campaign">("all");
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [dbUpdates, setDbUpdates] = useState<{ id: string; title: string; content: string; created_at: string; isNew?: boolean }[]>([]);
  const [dbNews, setDbNews] = useState<{ id: string; title: string; tag: string; created_at: string; isNew?: boolean }[]>([]);

  useEffect(() => {
    // Fetch updates from DB
    supabase
      .from("news")
      .select("id, title, content, created_at")
      .eq("tag", "アップデート")
      .eq("status", "published")
      .order("created_at", { ascending: false })
      .limit(5)
      .then(({ data }) => {
        if (data && data.length > 0) {
          const now = new Date();
          setDbUpdates(data.map(d => ({
            ...d,
            isNew: (now.getTime() - new Date(d.created_at).getTime()) < 7 * 24 * 60 * 60 * 1000,
          })));
        }
      });

    // Fetch news from DB
    supabase
      .from("news")
      .select("id, title, tag, created_at")
      .neq("tag", "アップデート")
      .eq("status", "published")
      .order("created_at", { ascending: false })
      .limit(10)
      .then(({ data }) => {
        if (data && data.length > 0) {
          const now = new Date();
          setDbNews(data.map(d => ({
            ...d,
            isNew: (now.getTime() - new Date(d.created_at).getTime()) < 7 * 24 * 60 * 60 * 1000,
          })));
        }
      });
  }, []);

  // Merge: DB news takes priority over mock
  const newsItems = dbNews.length > 0
    ? dbNews
    : mockNews.map(n => ({ ...n, created_at: n.date }));

  const filteredNews = activeNewsTab === "all"
    ? newsItems
    : newsItems.filter(n => n.tag === (activeNewsTab === "event" ? "イベント" : "キャンペーン"));

  const tagColor: Record<string, string> = {
    "特集": "bg-teal/10 text-teal",
    "イベント": "bg-gold/10 text-gold",
    "キャンペーン": "bg-deep-green/10 text-deep-green",
    "カテゴリ": "bg-stone-100 text-charcoal/50",
    "お知らせ": "bg-ivory text-charcoal/60",
  };

  return (
    <>
      <section className="bg-white py-20 px-4">
        <div className="max-w-screen-xl mx-auto">
          {/* Section header */}
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-gold text-xs font-display italic tracking-widest mb-2">Information</p>
              <h2 className="font-sans text-deep-green text-3xl font-light">お知らせ・最新情報</h2>
            </div>
            <button onClick={() => setShowPromoModal(true)} className="hidden md:inline-flex items-center gap-1 text-xs bg-deep-green text-white px-4 py-2 rounded-full font-sans cursor-pointer hover:bg-teal transition-colors whitespace-nowrap">
              <i className="ri-megaphone-line" />事業者の方：掲載申請
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* News - 3 columns */}
            <div className="lg:col-span-3">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-serif text-deep-green text-lg flex items-center gap-2">
                  <i className="ri-notification-3-line text-gold" />お知らせ
                </h3>
                <div className="flex gap-1 bg-stone-100 p-1 rounded-full">
                  {[{ v: "all" as const, l: "すべて" }, { v: "event" as const, l: "イベント" }, { v: "campaign" as const, l: "キャンペーン" }].map((t) => (
                    <button key={t.v} onClick={() => setActiveNewsTab(t.v)}
                      className={`px-3 py-1 rounded-full text-xs font-sans transition-all cursor-pointer whitespace-nowrap ${activeNewsTab === t.v ? "bg-white text-deep-green" : "text-charcoal/40 hover:text-charcoal"}`}>
                      {t.l}
                    </button>
                  ))}
                </div>
              </div>
              {filteredNews.length === 0 ? (
                <div className="py-10 text-center">
                  <i className="ri-notification-off-line text-3xl text-charcoal/20 block mb-2" />
                  <p className="text-sm text-charcoal/40 font-sans">現在お知らせはありません</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredNews.map((n) => (
                    <div key={n.id} className="flex items-start gap-3 p-4 rounded-2xl bg-stone-50 border-l-2 border-teal hover:bg-stone-100/50 transition-colors">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-sans whitespace-nowrap mt-0.5 flex-shrink-0 ${tagColor[n.tag] ?? "bg-stone-100 text-charcoal/50"}`}>{n.tag}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-charcoal font-sans leading-snug">{n.title}</p>
                        <p className="text-xs text-charcoal/40 font-sans mt-1">
                          {new Date(n.created_at).toLocaleDateString("ja-JP")}
                        </p>
                      </div>
                      {n.isNew && <span className="text-xs bg-gold text-white px-2 py-0.5 rounded-full font-sans whitespace-nowrap flex-shrink-0">NEW</span>}
                    </div>
                  ))}
                </div>
              )}
              {/* Promo request note */}
              <div className="mt-5 p-4 bg-ivory rounded-2xl border border-stone-100 flex items-center justify-between gap-4">
                <div>
                  <p className="text-xs font-sans font-medium text-charcoal">事業者様のイベント・特集掲載について</p>
                  <p className="text-xs text-charcoal/50 font-sans mt-0.5">申請フォームを送信いただくと、管理者が確認の上、掲載します。</p>
                </div>
                <button onClick={() => setShowPromoModal(true)} className="text-xs text-teal font-sans whitespace-nowrap cursor-pointer hover:underline flex-shrink-0">申請する →</button>
              </div>
            </div>

            {/* Updates - 2 columns */}
            <div className="lg:col-span-2">
              <h3 className="font-serif text-deep-green text-lg flex items-center gap-2 mb-5">
                <i className="ri-refresh-line text-teal" />アップデート情報
              </h3>
              {dbUpdates.length === 0 ? (
                <div className="bg-stone-50 rounded-2xl p-6 text-center">
                  <i className="ri-refresh-line text-3xl text-charcoal/20 block mb-2" />
                  <p className="text-sm text-charcoal/40 font-sans">アップデート情報はありません</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {dbUpdates.map((u) => (
                    <div key={u.id} className="bg-stone-50 rounded-2xl p-4 hover:bg-stone-100/50 transition-colors">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <p className="font-sans text-sm text-charcoal font-medium leading-snug">{u.title}</p>
                        {u.isNew && <span className="text-xs bg-gold text-white px-2 py-0.5 rounded-full font-sans whitespace-nowrap flex-shrink-0">NEW</span>}
                      </div>
                      {u.content && <p className="text-xs text-charcoal/50 font-sans leading-relaxed line-clamp-2">{u.content}</p>}
                      <p className="text-xs text-charcoal/30 font-sans mt-2">{new Date(u.created_at).toLocaleDateString("ja-JP")}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      {showPromoModal && <PromoRequestModal onClose={() => setShowPromoModal(false)} />}
    </>
  );
}

export function FreeSamplesSection() {
  const [samples, setSamples] = useState<{ id: string; title: string; type: string; price: number; thumbnail_url: string | null; category: string; provider?: { name: string; service_name: string | null } }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from("products")
      .select("id, title, type, price, thumbnail_url, category, provider:provider_id(name, service_name)")
      .eq("price", 0)
      .eq("status", "published")
      .limit(6)
      .then(({ data }) => {
        setSamples((data || []) as typeof samples);
        setLoading(false);
      });
  }, []);

  if (!loading && samples.length === 0) return null;

  return (
    <section className="bg-ivory py-20 px-4">
      <div className="max-w-screen-xl mx-auto">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-gold text-xs font-display italic tracking-widest mb-2">Free Samples</p>
            <h2 className="font-sans text-deep-green text-3xl md:text-4xl font-light">まず試してみる</h2>
            <p className="text-charcoal/50 text-sm font-sans mt-2">無料で体験できるサンプル講座・コンテンツ。購入前に技術レベルを確認できます。</p>
          </div>
          <Link to="/courses?free=1" className="text-teal text-sm font-sans hover:text-deep-green transition-colors whitespace-nowrap cursor-pointer hidden sm:block">無料講座をすべて見る →</Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(3)].map((_, i) => <div key={i} className="h-52 bg-stone-200 rounded-2xl animate-pulse" />)}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {samples.map(s => {
                const href = `/${s.type === "video" ? "courses" : "materials"}/${s.id}`;
                const providerName = (s.provider as { service_name?: string; name?: string } | null)?.service_name || (s.provider as { name?: string } | null)?.name || "講師";
                const placeholder = `https://public.readdy.ai/ai/img_res/ee5907bda1d38838bad3bf3b3240fc67.jpg traditional craft artisan tools materials elegant minimal no people&width=480&height=240&seq=sample-${s.id.slice(0,8)}&orientation=landscape`;
                return (
                  <Link key={s.id} to={href} className="group bg-white rounded-2xl overflow-hidden border border-stone-100 hover:-translate-y-1 transition-all duration-300 cursor-pointer block">
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={s.thumbnail_url || placeholder}
                        alt={s.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-deep-green/50 to-transparent" />
                      <div className="absolute top-3 left-3 flex items-center gap-2">
                        <span className="bg-teal text-white text-xs px-3 py-1 rounded-full font-sans font-medium">無料</span>
                        <span className="bg-white/90 text-charcoal text-xs px-2.5 py-1 rounded-full font-sans">
                          {s.type === "video" ? "動画講座" : "教材キット"}
                        </span>
                      </div>
                    </div>
                    <div className="p-5">
                      <p className="text-xs text-charcoal/40 font-sans mb-1.5">{providerName}</p>
                      <h3 className="font-sans text-charcoal text-sm font-medium leading-snug mb-3 line-clamp-2">{s.title}</h3>
                      <div className="flex items-center justify-between">
                        {s.category && <span className="tag-pill text-xs">{s.category}</span>}
                        <span className="text-teal text-xs font-sans font-medium group-hover:underline whitespace-nowrap">無料で試す →</span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
            <div className="mt-8 text-center sm:hidden">
              <Link to="/courses?free=1" className="btn-outline inline-block text-sm">無料講座をすべて見る →</Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export function FeaturedCategories() {
  const { categories, loading } = useCategories();

  return (
    <section className="bg-white py-20 px-4">
      <div className="max-w-screen-xl mx-auto">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-gold text-xs font-display italic tracking-widest mb-2">Categories</p>
            <h2 className="section-title text-3xl font-light">注目カテゴリ</h2>
          </div>
          <Link to="/categories" className="text-teal text-sm font-sans hover:text-deep-green transition-colors whitespace-nowrap cursor-pointer hidden sm:block">すべて見る →</Link>
        </div>
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => <div key={i} className="h-40 bg-stone-200 rounded-2xl animate-pulse" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {categories.slice(0, 8).map((cat) => (
              <Link key={cat.id} to={`/categories/${cat.id}`} className="group relative rounded-2xl overflow-hidden h-40 cursor-pointer block">
                <img src={cat.image} alt={cat.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-deep-green/85 via-deep-green/25 to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <p className="text-white font-serif text-sm font-medium">{cat.name}</p>
                </div>
                {cat.isNew && <div className="absolute top-3 right-3 bg-gold text-white text-xs px-2 py-0.5 rounded-full font-sans">NEW</div>}
              </Link>
            ))}
          </div>
        )}
        <div className="mt-6 text-center sm:hidden">
          <Link to="/categories" className="btn-outline inline-block text-sm">すべてのカテゴリ →</Link>
        </div>
      </div>
    </section>
  );
}
